// NOTE: HomeSchool.You has NO free tier as a product offering. Every tier is paid
// with a 7-day free trial. The `free` code below is an INTERNAL fallback state
// representing trial-expired or unpaid users — never market it as a tier.
//
// PRICING V3 (2026-07-13 launch): $20/mo minimum. Family $20/mo (4 seats) +
// Academy $35/mo (8 seats). Starter + $99-lifetime Founding Member retired.
// Before these new prices actually charge customers, the Stripe products/prices
// in the `create-checkout-session` edge function MUST be updated to match.
// Until then, the UI shows the new prices but Stripe still charges whatever
// its own price objects are configured for. Keep display + Stripe in sync.
export type Tier = 'free' | 'starter' | 'family' | 'academy' | 'launch';

/** Per-extra-seat overage price for Family and Academy (USD/mo) — requires matching Stripe subscription item. */
export const EXTRA_SEAT_MONTHLY = 5;

export interface TierLimits {
  maxChildren: number;
  hasProjects: boolean;
  hasCourses: boolean;
  hasReports: boolean;
  hasCompetitions: boolean;
  hasAdvancedDiagnostics: boolean;
  hasFieldTrips: boolean;
  hasGroupFeatures: boolean;
  hasFilingCenter: boolean;
  hasLocalHooks: boolean;
  hasAnnualReport: boolean;
  hasDataExport: boolean;
  hasCoopModes: boolean;
  hasLearnerAdaptations: boolean;
}

/** Grace period (days) after subscription period ends before offline downgrade */
export const OFFLINE_GRACE_DAYS = 3;

export const TIER_LIMITS: Record<Tier, TierLimits> = {
  free: {
    maxChildren: 1,
    hasProjects: false,
    hasCourses: false,
    hasReports: false,
    hasCompetitions: false,
    hasAdvancedDiagnostics: false,
    hasFieldTrips: false,
    hasGroupFeatures: false,
    hasFilingCenter: false,
    hasLocalHooks: false,
    hasAnnualReport: false,
    hasDataExport: false,
    hasCoopModes: false,
    hasLearnerAdaptations: false,
  },
  starter: {
    maxChildren: 1,
    hasProjects: true,
    hasCourses: true,
    hasReports: true,
    hasCompetitions: false,
    hasAdvancedDiagnostics: true,
    hasFieldTrips: true,
    hasGroupFeatures: false,
    hasFilingCenter: false,
    hasLocalHooks: true,
    hasAnnualReport: false,
    hasDataExport: false,
    hasCoopModes: false,
    hasLearnerAdaptations: false,
  },
  family: {
    maxChildren: 4,
    hasProjects: true,
    hasCourses: true,
    hasReports: true,
    hasCompetitions: false,
    hasAdvancedDiagnostics: true,
    hasFieldTrips: true,
    hasGroupFeatures: false,
    hasFilingCenter: true,
    hasLocalHooks: true,
    hasAnnualReport: true,
    hasDataExport: true,
    hasCoopModes: true,
    hasLearnerAdaptations: false,
  },
  academy: {
    maxChildren: 8,
    hasProjects: true,
    hasCourses: true,
    hasReports: true,
    hasCompetitions: true,
    hasAdvancedDiagnostics: true,
    hasFieldTrips: true,
    hasGroupFeatures: true,
    hasFilingCenter: true,
    hasLocalHooks: true,
    hasAnnualReport: true,
    hasDataExport: true,
    hasCoopModes: true,
    hasLearnerAdaptations: true,
  },
  // Launch tier: invite-only $20/mo flat price. Same feature set as Family.
  launch: {
    maxChildren: 4,
    hasProjects: true,
    hasCourses: true,
    hasReports: true,
    hasCompetitions: false,
    hasAdvancedDiagnostics: true,
    hasFieldTrips: true,
    hasGroupFeatures: false,
    hasFilingCenter: true,
    hasLocalHooks: true,
    hasAnnualReport: true,
    hasDataExport: true,
    hasCoopModes: true,
    hasLearnerAdaptations: false,
  },
};

export interface TierInfo {
  name: string;
  price: string;
  monthly: number;
  /** Annual price in USD — null means not offered at that tier. */
  annual: number | null;
  description: string;
}

export const TIER_INFO: Record<Tier, TierInfo> = {
  // `free` is the internal trial-expired / unpaid fallback state — NOT marketed.
  free: {
    name: 'Trial Expired',
    price: '—',
    monthly: 0,
    annual: null,
    description: 'Trial ended — choose a plan to continue learning.',
  },
  // Legacy tier — retired from public sale at launch (2026-07-13, $20/mo minimum).
  // Kept for type/DB compatibility with any pre-launch grants.
  starter: {
    name: 'Starter',
    price: '$9/mo',
    monthly: 9,
    annual: 79,
    description: '1 child, full curriculum, compliance tracker, and local ZIP hooks.',
  },
  family: {
    name: 'Family',
    price: '$20/mo',
    monthly: 20,
    annual: 199,
    description: 'Up to 4 children + Compliance Suite (filing-ready PDFs, annual progress reports, portfolio export).',
  },
  academy: {
    name: 'Academy',
    price: '$35/mo',
    monthly: 35,
    annual: 299,
    description: 'Up to 8 children + Coordination Suite (all-kids dashboard, sibling leaderboards, co-op invites).',
  },
  launch: {
    name: 'Launch',
    price: '$20/mo',
    monthly: 20,
    annual: null,
    description: 'Full platform access for up to 4 children. Invite-only.',
  },
};

export const FREE_TRIAL_DAYS = 7;
